import React, { useState, useEffect, useContext, ChangeEvent, useMemo } from 'react';
import Snackbar from '@material-ui/core/Snackbar';
import Alert from '@material-ui/lab/Alert';

import * as Components from './components';
import * as Contexts from '../../contexts';
import * as Buttons from '../../components/Buttons';
import * as Page from '../../components/Page';
import * as Form from '../../components/Form';
import { Switch } from 'antd';
import { TCoordinates, TFormTypes } from './types';
import {
  IRestaurant,
  TDeliveryTypes,
  TPaymentType,
  TTranslations,
  TWorkingTime,
} from '../../types/items';
import { useParams, useHistory } from 'react-router-dom';
import { config, Translater } from '../../config';
import { useHttp } from '../../hooks';
import { useValidation } from '../../hooks';
import { TabLanguage } from '../../components/TabLanguage';
import { getStringFromTranslationsArr } from '../../helpers';
import { WorkingTable } from './components/WorkingTable/WorkingTable';
import { DisabledBookingDays } from './components/DisabledBookingDays/DisabledBookingDays';

function stripIds(timeObj: any): TWorkingTime {
  const days: (keyof TWorkingTime)[] = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  return days.reduce((acc, day) => {
    acc[day] = timeObj[day]
      ? { start: timeObj[day].start, end: timeObj[day].end }
      : { start: '', end: '' };
    return acc;
  }, {} as TWorkingTime);
}

const DetailPage: React.FC = () => {
  const { token } = useContext(Contexts.AuthContext);
  const { access } = useContext(Contexts.UserContext);
  const { language } = useContext(Contexts.LanguageContext);

  const history = useHistory();
  const { id } = useParams<{ id: string }>();
  const { loading, request } = useHttp();

  const [primary, setPrimary] = useState<IRestaurant | null>(null);
  const [headerPhoto, setHeaderPhoto] = useState<File | null | any>(null);
  const [photo, setPhoto] = useState<File | null>(null);
  const [pdf, setPdf] = useState<File | null>(null);
  const [workingTime, setWorkingTime] = useState<TWorkingTime>();
  const [deliveryTime, setDeliveryTime] = useState<TWorkingTime>();
  const [valueLang, setValueLang] = useState<string>(language.slug);

  const [metaKeywords, setMetaKeywords] = useState<string[]>([]);
  const [metaDescription, setMetaDescription] = useState<string>('');

  const [isClicked, setIsClicked] = useState<boolean>(false);
  const [form, setForm] = useState<any>({
    city: '',
    login: '',
    title: [{ value: '', lang: '' }],
    password: '',
    email: '',
    phone: '',
    facebook: '',
    instagram: '',
    telegram: '',
    headerColor: '',
    order: '',
    enableBasket: false,
    show: false,
    hasDelivery: '',
    minimalOrder: 0,
  });
  const [accessRules, setAccessRules] = useState({
    hideTaxiDelivery: false,
    hidePickup: false,
    hideAddressDelivery: false,
    hideCashPayment: false,
    hideOnlinePayment: false,
    enableBanketMenu: false,
  });
  const [address, setAddress] = useState<{ value: string; lang: string }[]>([]);
  const [coordinates, setCoordinates] = useState<TCoordinates>({
    lat: '0',
    lon: '0',
  });
  const [isAlertOpen, toogleIsAlertOpen] = useState<boolean>(false);
  const allTimeFilled = (time: TWorkingTime | undefined) =>
    !!time && Object.values(time).every((day) => !!day.start && !!day.end);

  const isDeliveryTimeInsideWorkingTime = (
    work: TWorkingTime | undefined,
    delivery: TWorkingTime | undefined,
  ): boolean => {
    if (!work || !delivery) return false;

    return (Object.keys(work) as (keyof TWorkingTime)[]).every((day) => {
      const w = work[day];
      const d = delivery[day];

      if (!w?.start || !w?.end || !d?.start || !d?.end) return false;

      return d.start >= w.start && d.end <= w.end;
    });
  };

  const schema = useMemo<any>(
    () => ({
      title: {
        condition:
          form.title
            .map((item: any) => {
              if (!item.value) {
                return false;
              }
              return true;
            })
            .filter((item: any) => item).length == 2,
        error: `
          ${Translater.ErrorLanguage[language.slug]}:
          ${Translater.TableTitles.title[language.slug]}`,
      },
      photo: {
        condition: true,
        error: `
        ${Translater.ErrorPhoto[language.slug]}:
        ${Translater.TableTitles.phone[language.slug]}`,
      },
      phone: {
        condition: !!form.phone,
        error: `
          ${Translater.ErrorLength[language.slug]}:
          ${Translater.TableTitles.phone[language.slug]}`,
      },
      // email: {
      //   condition: !!form.email,
      //   error: `
      //     ${Translater.ErrorLength[language.slug]}:
      //     ${Translater.TableTitles.email[language.slug]}`,
      // },
      value: {
        condition: !!address,
        error: `
          ${Translater.ErrorAddress[language.slug]}:
          ${Translater.TableTitles.address[language.slug]}`,
      },
      workingTime: {
        condition: allTimeFilled(workingTime),
        error: `
        ${Translater.TableTitles.errorWorkingTime[language.slug]}:
        ${Translater.TableTitles.workTime[language.slug]}
      `,
      },
      deliveryTime: {
        condition:
          allTimeFilled(deliveryTime) && isDeliveryTimeInsideWorkingTime(workingTime, deliveryTime),
        error: `
        ${Translater.TableTitles.errorWorkingTime[language.slug]}:
        ${Translater.TableTitles.orderAcceptTime[language.slug]}
      `,
      },
    }),
    [form.title, form.phone, language.slug, address, workingTime, deliveryTime],
  );

  const { errors, validationSchema, validation } = useValidation(schema);

  const Events = {
    inputHandler: (e: ChangeEvent<HTMLInputElement>) => {
      const name = e.target.name;
      const value = e.target.value;

      if (e.target.name === 'title') {
        const isTitleInArr = form.title.find((item: any) => item.lang == valueLang) ? true : false;

        if (isTitleInArr) {
          const title = form.title
            .map((item: any) => {
              if (item.lang == valueLang) {
                return { value: e.target.value, lang: valueLang };
              }
              return item;
            })
            .filter((item: any) => item.value);

          setForm({
            ...form,
            title: title as { value: string; lang: string }[],
          });

          return;
        }

        const title = form.title
          .concat({
            value: e.target.value,
            lang: valueLang,
          })
          .filter((item: any) => item.value);

        setForm({
          ...form,
          title: title as { value: string; lang: string }[],
        });

        return;
      }
      if (e.target.name === 'pdf') {
        setPdf(e.target?.files?.length ? e.target?.files[0] : null);
        return;
      }

      if (e.target.name === 'photo') {
        setPhoto(e.target?.files?.length ? e.target?.files[0] : null);
        return;
      } else setForm({ ...form, [name]: value });
    },
    inputFileHandler: (e: ChangeEvent<HTMLInputElement>) => {
      setHeaderPhoto(e.target?.files?.length ? e.target?.files[0] : null);
    },
    selectHandler: (e: ChangeEvent<HTMLSelectElement>) => {
      setForm({ ...form, [e.target.name]: e.target.value });
    },
    saveHandler: async () => {
      try {
        setIsClicked(true);
        await validation();

        Callbacks.Save();
      } catch (e) {
        toogleIsAlertOpen(true);
      }
    },
    deleteHandler: () => {
      const answer = window.confirm(Translater.Alert.delete[language.slug]);
      if (answer) Callbacks.Delete();
    },
  };
  const IS_BABOGARDENS=primary?.slug==='babo-gardens'
  const Callbacks = {
    Fetch: async () => {
      try {
        const response: IRestaurant = await request(
          `${config.API}/restaurants/${id}`,
          'GET',
          null,
          {
            Authorization: `Bearer ${token as string}`,
          },
        );

        if (response) {
          setPrimary(response);
        }

        const newForm = {
          ...form,
          ...(response.title ? { title: response.title } : {}),
          ...(response?.contacts?.email ? { email: response?.contacts?.email } : {}),
          ...(response?.contacts?.phone ? { phone: response?.contacts?.phone } : {}),
          ...(response?.contacts?.social?.facebook
            ? { facebook: response?.contacts?.social?.facebook }
            : {}),
          ...(response?.contacts?.social?.telegram
            ? { telegram: response?.contacts?.social?.telegram }
            : {}),
          ...(response?.contacts?.social?.instagram
            ? { instagram: response?.contacts?.social?.instagram }
            : {}),
          ...(response?.headerColor ? { headerColor: response?.headerColor } : {}),
          ...(response?.hasDelivery ? { hasDelivery: response?.hasDelivery } : {}),
          enableBasket: !!response.enableBasket,
          show: !!response.show,
          minimalOrder: response.minimalOrder || 0,
        };
        setCoordinates({
          lon: response.location.coordinates[0] + '',
          lat: response.location.coordinates[1] + '',
        });
        setWorkingTime(stripIds(response.workingTime));
        setDeliveryTime(stripIds(response.deliveryTime));
        setAddress(response?.address);
        setForm(newForm);
        response.metaDescription && setMetaDescription(response.metaDescription);
        response.metaKeywords && setMetaKeywords(response.metaKeywords);
        const deliveries = response.availableDeliveries ?? [];
        const payments = response.availablePaymentTypes ?? [];

        setAccessRules((prev) => ({
          ...prev,
          enableBanketMenu: !!response.enableBanketMenu,
          hidePickup: deliveries.length ? !deliveries.includes('pickup') : prev.hidePickup,
          hideAddressDelivery: deliveries.length
            ? !deliveries.includes('courier')
            : prev.hideAddressDelivery,
          hideTaxiDelivery: deliveries.length
            ? !deliveries.includes('taxi')
            : prev.hideTaxiDelivery,

          hideCashPayment: payments.length ? !payments.includes('cash') : prev.hideCashPayment,
          hideOnlinePayment: payments.length
            ? !payments.includes('online')
            : prev.hideOnlinePayment,
        }));
      } catch (e) {}
    },
    Save: async () => {
      try {
        const {
          title,
          email,
          phone,
          facebook,
          instagram,
          telegram,
          enableBasket,
          show,
          minimalOrder,
        } = form;

        const contacts: IRestaurant['contacts'] = {
          phone,
          email,
          social: {
            facebook,
            instagram,
            telegram,
          },
        };
        const data: FormData = new FormData();

        const availablePaymentTypes: TPaymentType[] = [];
        const availableDeliveries: TDeliveryTypes[] = [];

        if (!accessRules.hideCashPayment) availablePaymentTypes.push('cash');
        if (!accessRules.hideOnlinePayment) availablePaymentTypes.push('online');

        if (!accessRules.hidePickup) availableDeliveries.push('pickup');
        if (!accessRules.hideAddressDelivery) availableDeliveries.push('courier');
        if (!accessRules.hideTaxiDelivery) availableDeliveries.push('taxi');
       if(IS_BABOGARDENS)
          {data.append('enableBanketMenu', accessRules.enableBanketMenu + '')}
       
        data.append('availableDeliveries', JSON.stringify(availableDeliveries));
        data.append('availablePaymentTypes', JSON.stringify(availablePaymentTypes));
        data.append('contacts', JSON.stringify(contacts));
        pdf && data.append('offlineMenu', pdf as Blob);
        data.append('address', JSON.stringify(address));
        data.append('workingTime', JSON.stringify(workingTime));
        data.append('deliveryTime', JSON.stringify(deliveryTime));
        data.append('show', show + '');
        data.append('enableBasket', enableBasket + '');
        data.append('minimalOrder', minimalOrder);
 
    
        photo && data.append('mainImage', photo as Blob);
        coordinates &&
          data.append(
            'location',
            JSON.stringify({
              coordinates: [+coordinates.lon, +coordinates.lat],
              type: 'Point',
            }),
          );
        data.append('title', JSON.stringify(form?.title));
        data.append('hasDelivery', form?.hasDelivery);

        data.append('metaDescription', metaDescription);
        if (metaKeywords.length) {
          metaKeywords.map((el) => data.append('metaKeywords[]', el));
        }

        const response = await request(`${config.API}/restaurants/${id}`, 'POST', data, {
          Authorization: `Bearer ${token as string}`,
        });

        if (response) history.push('/restaurants');
      } catch (e) {}
    },
    Delete: async () => {
      try {
        await request(`${config.API}/restaurants/${id}`, 'DELETE', null, {
          Authorization: `Bearer ${token as string}`,
        });

        history.push('/restaurants');
      } catch (e) {
        console.log(e);
      }
    },
  };
  useEffect(() => {
    Callbacks.Fetch();
  }, [id]);

  const formToggles = [
    {
      key: 'show',
      labelKey: 'showRestaurant',
      checked: !!form.show,
      toggle: () => setForm((p: any) => ({ ...p, show: !p.show })),
    },
    {
      key: 'enableBasket',
      labelKey: 'addToCart',
      checked: !!form.enableBasket,
      toggle: () => setForm((p: any) => ({ ...p, enableBasket: !p.enableBasket })),
    },
  ] as const;

  const accessToggles = [
    {
      key: 'hideTaxiDelivery',
      labelKey: 'hideTaxiDelivery',
      checked: !!accessRules.hideTaxiDelivery,
      toggle: () => setAccessRules((p) => ({ ...p, hideTaxiDelivery: !p.hideTaxiDelivery })),
    },
    {
      key: 'hidePickup',
      labelKey: 'hidePickup',
      checked: !!accessRules.hidePickup,
      toggle: () => setAccessRules((p) => ({ ...p, hidePickup: !p.hidePickup })),
    },
    {
      key: 'hideAddressDelivery',
      labelKey: 'hideAddressDelivery',
      checked: !!accessRules.hideAddressDelivery,
      toggle: () => setAccessRules((p) => ({ ...p, hideAddressDelivery: !p.hideAddressDelivery })),
    },
    {
      key: 'hideCashPayment',
      labelKey: 'hideCashPayment',
      checked: !!accessRules.hideCashPayment,
      toggle: () => setAccessRules((p) => ({ ...p, hideCashPayment: !p.hideCashPayment })),
    },
    {
      key: 'hideOnlinePayment',
      labelKey: 'hideOnlinePayment',
      checked: !!accessRules.hideOnlinePayment,
      toggle: () => setAccessRules((p) => ({ ...p, hideOnlinePayment: !p.hideOnlinePayment })),
    },
  ...(IS_BABOGARDENS ? [{
    key: 'enableBanketMenu',
    labelKey: 'enableBanketMenu',
    checked: !!accessRules.enableBanketMenu,
    toggle: () => setAccessRules((p) => ({ ...p, enableBanketMenu: !p.enableBanketMenu })),
  }] as const : []),
  ] as const;

  const toggles = [...formToggles, ...accessToggles];
  return (
    <Page.Wrapper
      title={getStringFromTranslationsArr(primary?.title as TTranslations, language.slug)}
    >
      <Page.Header
        backButtonTitle={Translater.RestaurantsDetailPage.title[language.slug]}
        backButtonLink="/restaurants"
      />

      <Buttons.Container
        disabled={loading}
        saveHandler={access.restaurants?.change ? Events.saveHandler : false}
        // deleteHandler={
        //   access.restaurants?.rule === "change" ? Events.deleteHandler : false
        // }
      />

      <TabLanguage setValueLang={setValueLang} valueLang={valueLang} />
      <div style={{ margin: '20px 0' }}>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: '12px 16px',
            alignItems: 'center',
            maxWidth: 520,
          }}
        >
          {toggles.map((item) => (
            <div
              key={item.key}
              style={{
                display: 'contents',
              }}
            >
              <p style={{ margin: 0 }}>
                {Translater.RestaurantsDetailPage[item.labelKey][language.slug]}
              </p>

              <Switch checked={item.checked} onChange={item.toggle} />
            </div>
          ))}
        </div>
      </div>
      <DisabledBookingDays restaurant={id} token={token} />
      <Components.ConfigBlock
        form={form}
        metaKeywords={metaKeywords}
        setMetaKeywords={setMetaKeywords}
        metaDescription={metaDescription}
        setMetaDescription={setMetaDescription}
        photo={photo}
        data={primary ?? undefined}
        address={address}
        headerPhoto={headerPhoto}
        setForm={setForm as any}
        isClicked={isClicked}
        inputFileHandler={Events.inputFileHandler}
        inputHandler={Events.inputHandler}
        valueLang={valueLang}
        setPhoto={setPhoto}
      />
      <div
        style={{
          maxWidth: '712px',
          display: 'flex',
          gap: '20px',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
        }}
      >
        <WorkingTable
          initialData={workingTime}
          setData={setWorkingTime}
          title={Translater.Landings.Banquet.workingHours[language.slug]}
        />
        <WorkingTable
          initialData={deliveryTime}
          setData={setDeliveryTime}
          title={Translater.TableTitles.orderAcceptTime[language.slug]}
        />
      </div>

      <Components.ContactsBlock
        form={form}
        setForm={setForm}
        isClicked={isClicked}
        setAddress={setAddress}
        address={address}
        valueLang={valueLang}
        setCoordinates={setCoordinates}
        isValid={validationSchema}
        inputHandler={Events.inputHandler}
      />

      <Form.DateBlock
        updated={primary?.updatedAt as string}
        created={primary?.createdAt as string}
      />
      <Snackbar
        open={isAlertOpen}
        autoHideDuration={10000}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        onClose={() => toogleIsAlertOpen(false)}
      >
        <Alert severity="error">
          {errors.map((error) => (
            <p key={`error-item-${error}`}>- {error}</p>
          ))}
        </Alert>
      </Snackbar>
    </Page.Wrapper>
  );
};

export default DetailPage;
